import {
  GalleryAlbumEntry,
  GalleryAlbumManifest,
  GalleryIndexManifest,
  GalleryPhotoEntry,
  MANIFEST_VERSION,
  folderOf,
  isCoverName,
} from "./galleryPaths";

// Cover first, then newest first. Must stay identical to the backfill script's
// ordering — clients render the array as given and never re-sort.
const sortPhotos = (photos: GalleryPhotoEntry[]): GalleryPhotoEntry[] =>
  [...photos].sort((a, b) => {
    if (a.cover !== b.cover) return a.cover ? -1 : 1;
    return b.timeCreated.localeCompare(a.timeCreated);
  });

const toEntry = (path: string, timeCreated: string): GalleryPhotoEntry => ({
  path,
  folder: folderOf(path),
  timeCreated: timeCreated || "",
  cover: isCoverName(path.split("/").pop() ?? ""),
});

export const emptyAlbumManifest = (
  album: string,
  generatedAt: string
): GalleryAlbumManifest => ({
  version: MANIFEST_VERSION,
  album,
  generatedAt,
  photos: [],
});

export const addPhotos = (
  manifest: GalleryAlbumManifest,
  additions: Array<{ path: string; timeCreated: string }>,
  generatedAt: string
): GalleryAlbumManifest => {
  const added = additions.map((a) => toEntry(a.path, a.timeCreated));
  const addedPaths = new Set(added.map((a) => a.path));
  const kept = manifest.photos.filter((p) => !addedPaths.has(p.path));

  return {
    ...manifest,
    generatedAt,
    photos: sortPhotos([...kept, ...added]),
  };
};

export const removePhoto = (
  manifest: GalleryAlbumManifest,
  path: string,
  generatedAt: string
): GalleryAlbumManifest => ({
  ...manifest,
  generatedAt,
  photos: manifest.photos.filter((p) => p.path !== path),
});

export const renamePhoto = (
  manifest: GalleryAlbumManifest,
  fromPath: string,
  toPath: string,
  generatedAt: string
): GalleryAlbumManifest => {
  const existing = manifest.photos.find((p) => p.path === fromPath);
  const rest = manifest.photos.filter((p) => p.path !== fromPath && p.path !== toPath);
  const moved = toEntry(toPath, existing?.timeCreated ?? generatedAt);

  return { ...manifest, generatedAt, photos: sortPhotos([...rest, moved]) };
};

export const summariseAlbum = (
  manifest: GalleryAlbumManifest
): GalleryAlbumEntry | null => {
  if (manifest.photos.length === 0) return null;
  const cover = manifest.photos.find((p) => p.cover) ?? manifest.photos[0];
  const latest = manifest.photos.reduce(
    (max, p) => (p.timeCreated > max ? p.timeCreated : max),
    ""
  );
  return {
    name: manifest.album,
    count: manifest.photos.length,
    cover: cover ? cover.path : null,
    latest,
  };
};

export const patchIndex = (
  index: GalleryIndexManifest,
  album: string,
  entry: GalleryAlbumEntry | null,
  generatedAt: string
): GalleryIndexManifest => {
  const others = index.albums.filter((a) => a.name !== album);
  const albums = (entry ? [...others, entry] : others).sort(
    (a, b) => b.latest.localeCompare(a.latest) || a.name.localeCompare(b.name)
  );
  return { version: MANIFEST_VERSION, generatedAt, albums };
};
